import ReportHeader from "./ReportHeader";
import ReportStyle from "./ReportStyle";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Svg,
  Circle,
  Path,
} from "@react-pdf/renderer";
import { tokens } from "../../theme";
import {
  REPORT_ACTIVITY_SECTIONS,
  REPORT_ACTIVITY_YEAR_TITLE,
  REPORT_SUMMARY_CARDS,
} from "../../utils/mapers";
import {
  formatDate,
  getItemYear,
  isWithinYearRange,
  normalizeContentFilter,
  normalizeText,
  plural,
  valueOrDash,
} from "../../utils/utils";

const colors = tokens();

const styles = StyleSheet.create({
  page: {
    paddingTop: 58,
    paddingBottom: 34,
    paddingHorizontal: 0,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: colors.primary[100],
  },

  content: {
    paddingHorizontal: 30,
    paddingTop: 12,
  },

  pageNumber: {
    position: "absolute",
    bottom: 9.5,
    right: 32,
    fontSize: 9,
    color: colors.primary[200],
  },

  subtitle: {
    fontSize: 10,
    marginBottom: 8,
    color: colors.primary[100],
  },

  section: {
    marginBottom: 12,
    paddingBottom: 2,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    borderBottomStyle: "solid",
  },

  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
    color: colors.green[200],
  },

  yearTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 6,
    color: colors.primary[100],
  },

  subYearTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 4,
    color: colors.grey[600],
  },

  row: {
    flexDirection: "row",
    marginBottom: 1.5,
  },

  rowLabel: {
    fontWeight: "bold",
    color: colors.primary[100],
  },

  rowValue: {
    flexShrink: 1,
    color: colors.primary[100],
  },

  descriptionBlock: {
    marginTop: 2,
  },

  descriptionLabel: {
    fontWeight: "bold",
    marginBottom: 1,
    color: colors.primary[100],
  },

  descriptionText: {
    lineHeight: 0.7,
    color: colors.primary[100],
  },

  twoCols: {
    flexDirection: "row",
    gap: 16,
  },

  col: {
    flex: 1,
  },

  card: {
    marginBottom: 6,
    padding: 6,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderStyle: "solid",
    borderRadius: 4,
  },

  cardTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 4,
    color: colors.primary[100],
    lineHeight: 1.2,
  },

  empty: {
    fontFamily: "Helvetica",
    color: colors.grey[600],
  },

  summaryCardsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 4,
    marginBottom: 8,
  },

  summaryCard: {
    width: "31.5%",
    minHeight: 46,
    marginRight: 6,
    marginBottom: 6,
    paddingVertical: 7,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderStyle: "solid",
    borderRadius: 6,
    backgroundColor: colors.primary[200],
  },

  summaryCardValue: {
    fontSize: 15,
    fontWeight: "bold",
    color: colors.green[200],
    marginBottom: 2,
  },

  summaryCardLabel: {
    fontSize: 8.5,
    lineHeight: 1.2,
    color: colors.primary[100],
  },

  summarySubsection: {
    marginTop: 4,
    paddingTop: 2,
  },

  summarySubsectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: colors.primary[100],
    marginBottom: 4,
  },

  summaryBulletRow: {
    flexDirection: "row",
    marginBottom: 3,
  },

  summaryBullet: {
    width: 10,
    fontSize: 9,
    color: colors.orange[200],
  },

  summaryBulletText: {
    flex: 1,
    fontSize: 9,
    lineHeight: 1.25,
    color: colors.primary[100],
  },

  standardSection: {
    marginTop: 6,
  },

  standardGrid: {
    flexDirection: "row",
    gap: 18,
    alignItems: "flex-start",
  },

  standardCol: {
    flex: 1,
  },

  standardTitle: {
    fontSize: 9.5,
    fontWeight: "bold",
    color: colors.primary[100],
    marginBottom: 3,
  },

  yearBlock: {
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    borderBottomStyle: "solid",
  },

  yearBlockLast: {
    marginBottom: 0,
    paddingBottom: 0,
  },

  itemBullet: {
    marginBottom: 1,
    lineHeight: 1.25,
    color: colors.primary[100],
  },

  periodRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },

  periodText: {
    fontSize: 10,
    color: colors.primary[100],
  },

  infoGeneralRow: {
    flexDirection: "row",
    gap: 16,
    alignItems: "flex-start",
  },

  photoCol: {
    width: 90,
    alignItems: "center",
  },

  photoBox: {
    width: 82,
    height: 82,
    borderRadius: 41,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderStyle: "solid",
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: colors.grey[100],
  },

  profileImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  photoPlaceholder: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },

  photoPlaceholderIcon: {
    width: "100%",
    height: "100%",
    color: colors.grey[500],
    fill: colors.grey[100],
  },

  infoCol: {
    flex: 1,
  },
});

const fechaEmision = new Date().toLocaleDateString("es-CL", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const anioActual = new Date().getFullYear();

function hasText(value) {
  const text = String(value ?? "").trim();

  return (
    text &&
    !["—", "-", "none", "no", "sin informacion"].includes(normalizeText(text))
  );
}

function countValues(value) {
  return String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item && item !== "—" && item !== "-").length;
}

function getPeriodoLabel(yearFrom, yearTo, disciplina) {
  if (yearFrom && yearTo && Number(yearFrom) === Number(yearTo)) {
    return String(yearFrom);
  }

  if (yearFrom && yearTo) return `${yearFrom}–${yearTo}`;
  if (yearFrom) return `Desde ${yearFrom}`;
  if (yearTo) return `Hasta ${yearTo}`;

  const disciplinaNormalizada = normalizeText(disciplina);

  if (
    disciplinaNormalizada.includes("matematica") ||
    disciplinaNormalizada.includes("parvularia")
  ) {
    return `2022–${anioActual}`;
  }

  if (disciplinaNormalizada.includes("lenguaje")) return `2025–${anioActual}`;

  return `Hasta ${anioActual}`;
}

function extractRomanEncounter(value) {
  return (
    normalizeText(value)
      .match(/\b(i|ii|iii|iv|v|vi|vii|viii|ix|x)\b\s+encuentro/)?.[1]
      ?.toUpperCase() ?? null
  );
}

function enrichPresentacionesWithYear(presentaciones = [], encuentros = []) {
  return presentaciones.map((presentacion) => {
    if (typeof presentacion !== "string") return presentacion;

    const roman = extractRomanEncounter(presentacion);
    const normalizedPresentation = normalizeText(presentacion).replace(/^el\s+/, "");

    const matchedEncuentro = encuentros.find((encuentro) => {
      const encRoman =
        extractRomanEncounter(encuentro?.titulo) ||
        extractRomanEncounter(encuentro?.descripcion);

      return (
        (roman && encRoman && roman === encRoman) ||
        normalizeText(encuentro?.titulo).includes(normalizedPresentation) ||
        normalizeText(encuentro?.descripcion).includes(normalizedPresentation)
      );
    });

    return {
      titulo: presentacion,
      anio: matchedEncuentro ? getItemYear(matchedEncuentro) : null,
      fecha: matchedEncuentro?.inicio ?? null,
      encuentro_id: matchedEncuentro?.id ?? null,
    };
  });
}

function groupYearActivities(groups) {
  const map = new Map();
  const emptyYear = () =>
    Object.fromEntries(REPORT_ACTIVITY_SECTIONS.map(({ key }) => [key, []]));

  Object.entries(groups).forEach(([key, items]) => {
    (items ?? []).forEach((item) => {
      const year = getItemYear(item);
      if (!year) return;

      if (!map.has(year)) map.set(year, { year, ...emptyYear() });
      map.get(year)[key].push(item);
    });
  });

  return Array.from(map.values()).sort((a, b) => b.year - a.year);
}

function cleanDescription(value) {
  return String(value ?? "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li>/gi, "• ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function FieldRow({ label, children }) {
  return (
    <Text style={styles.row}>
      <Text style={styles.rowLabel}>{label}</Text>
      {children}
    </Text>
  );
}

function EventCard({ item, type }) {
  return (
    <View style={styles.card} wrap={false}>
      <Text style={styles.cardTitle}>
        {valueOrDash(item?.titulo || item?.descripcion || item?.nombre || item?.title)}
      </Text>

      {item?.inicio ? (
        <View style={styles.row}>
          <Text style={styles.rowLabel}>Fecha: </Text>
          <Text style={styles.rowValue}>{formatDate(item.inicio)}</Text>
        </View>
      ) : null}

      {type === "encuentro" && item?.lugar && item.lugar !== "—" ? (
        <View style={styles.row}>
          <Text style={styles.rowLabel}>Lugar: </Text>
          <Text style={styles.rowValue}>{valueOrDash(item.lugar)}</Text>
        </View>
      ) : null}

      {type !== "encuentro" && item?.expositor && item.expositor !== "—" ? (
        <View style={styles.row}>
          <Text style={styles.rowLabel}>Expositor/a: </Text>
          <Text style={styles.rowValue}>{valueOrDash(item.expositor)}</Text>
        </View>
      ) : null}

      {item?.descripcion && item.descripcion !== "—" ? (
        <View style={styles.descriptionBlock}>
          <Text style={styles.descriptionLabel}>Descripción:</Text>
          <Text style={styles.descriptionText}>
            {cleanDescription(item.descripcion)}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

function CourseCard({ item }) {
  return (
    <View style={styles.card} wrap={false}>
      <Text style={styles.cardTitle}>{valueOrDash(item?.nombre)}</Text>

      <View style={styles.row}>
        <Text style={styles.rowLabel}>Versión: </Text>
        <Text style={styles.rowValue}>{valueOrDash(item?.version)}</Text>
      </View>

      <View style={styles.row}>
        <Text style={styles.rowLabel}>Disciplina: </Text>
        <Text style={styles.rowValue}>{valueOrDash(item?.disciplina)}</Text>
      </View>
    </View>
  );
}

function ActivityItem({ config, item }) {
  if (config.kind === "course") return <CourseCard item={item} />;
  if (config.kind === "event") return <EventCard item={item} type={config.type} />;

  return (
    <Text style={styles.itemBullet}>
      • {valueOrDash(
        typeof item === "string"
          ? item
          : item?.titulo || item?.nombre || item?.descripcion
      )}
    </Text>
  );
}

function ActivityGroup({ config, items, skipFirst = false }) {
  const renderedItems = skipFirst ? items.slice(1) : items;
  if (!renderedItems.length) return null;

  if (skipFirst) {
    return (
      <View style={{ marginBottom: config.kind === "list" ? 3 : 8 }}>
        {renderedItems.map((item, idx) => (
          <ActivityItem
            key={`${config.key}-${item?.id ?? item?.titulo ?? item?.descripcion ?? idx}`}
            config={config}
            item={item}
          />
        ))}
      </View>
    );
  }

  const [first, ...rest] = renderedItems;

  return (
    <View style={{ marginBottom: config.kind === "list" ? 3 : 8 }}>
      <View wrap={false}>
        <Text style={styles.subYearTitle}>{config.title}</Text>
        <ActivityItem config={config} item={first} />
      </View>

      {rest.map((item, idx) => (
        <ActivityItem
          key={`${config.key}-${item?.id ?? item?.titulo ?? item?.descripcion ?? idx}`}
          config={config}
          item={item}
        />
      ))}
    </View>
  );
}

function SummaryCard({ value, singular, pluralLabel }) {
  return (
    <View style={styles.summaryCard} wrap={false}>
      <Text style={styles.summaryCardValue}>{value}</Text>
      <Text style={styles.summaryCardLabel}>
        {plural(value, singular, pluralLabel)}
      </Text>
    </View>
  );
}

export default function ReportDocument({
  persona,
  yearFrom = null,
  yearTo = null,
  contentFilter = ["general"],
}) {
  if (!persona) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <ReportStyle disciplina={persona?.disciplina} />

          <View style={styles.content}>
            <ReportHeader
              title="RESUMEN DE PARTICIPACIÓN EN RedFID"
              subtitle="Usuario no encontrado"
            />

            <Text>No fue posible cargar los datos del usuario seleccionado.</Text>
          </View>

          <Text
            fixed
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
            style={styles.pageNumber}
          />
        </Page>
      </Document>
    );
  }

  const selectedContent = normalizeContentFilter(contentFilter);
  const isGeneralContent = selectedContent.includes("general");

  const showCursos = isGeneralContent || selectedContent.includes("cursos");
  const showEncuentros = isGeneralContent || selectedContent.includes("encuentros");
  const showTalleres = isGeneralContent || selectedContent.includes("talleres");
  const showWebinars = isGeneralContent || selectedContent.includes("webinars");
  const showReuniones = isGeneralContent || selectedContent.includes("reuniones");

  const showOnlyReuniones =
    selectedContent.length === 1 && selectedContent[0] === "reuniones";

  const activeActivitySections = REPORT_ACTIVITY_SECTIONS.filter(
    ({ content }) => isGeneralContent || selectedContent.includes(content)
  );

  const activityByYearTitle =
    isGeneralContent || selectedContent.length !== 1
      ? "Actividades realizadas por año"
      : REPORT_ACTIVITY_YEAR_TITLE[selectedContent[0]] ?? "Actividades realizadas por año";

  const talleresFiltrados = showTalleres
    ? (persona.talleres ?? []).filter((item) =>
      isWithinYearRange(item, yearFrom, yearTo)
    )
    : [];

  const talleresRelatadosFiltrados = showTalleres
    ? (persona.talleres_expuestos ?? []).filter((item) =>
      isWithinYearRange(item, yearFrom, yearTo)
    )
    : [];

  const encuentrosFiltrados = showEncuentros
    ? (persona.encuentros ?? []).filter((item) =>
      isWithinYearRange(item, yearFrom, yearTo)
    )
    : [];

  const encuentrosNacionalesFiltrados = encuentrosFiltrados.filter((item) => {
    const tipo = normalizeText(item?.tipo_encuentro);
    const bucket = normalizeText(item?.bucket ?? item?.subcategoria);

    return tipo === "nacional" || bucket === "encuentro-nacional";
  });

  const encuentrosTelematicosFiltrados = encuentrosFiltrados.filter((item) => {
    const tipo = normalizeText(item?.tipo_encuentro);
    const bucket = normalizeText(item?.bucket ?? item?.subcategoria);

    return (
      tipo === "virtual" ||
      tipo === "telematico" ||
      bucket === "encuentro-virtual" ||
      bucket === "encuentro-telematico"
    );
  });

  const reunionesComiteFiltradas = showReuniones
    ? (persona.reuniones_comite ?? []).filter((item) =>
      isWithinYearRange(item, yearFrom, yearTo)
    )
    : [];

  const reunionesVirtualesFiltradas = showReuniones
    ? (persona.reuniones_virtuales ?? []).filter((item) =>
      isWithinYearRange(item, yearFrom, yearTo)
    )
    : [];

  const webinarsFiltrados = showWebinars
    ? (persona.webinars ?? []).filter((item) =>
      isWithinYearRange(item, yearFrom, yearTo)
    )
    : [];

  const presentacionesFiltradas = showEncuentros
    ? enrichPresentacionesWithYear(
      persona.presentaciones ?? [],
      persona.encuentros ?? []
    ).filter((item) => isWithinYearRange(item, yearFrom, yearTo))
    : [];

  const cursosFiltrados = showCursos
    ? (persona.cursos ?? [])
      .filter((item) => {
        const estado = String(item?.estado ?? "").trim().toLowerCase();

        return (
          item?.aprobado === true ||
          estado === "aprobado" ||
          (item?.aprobado === undefined && item?.estado === undefined)
        );
      })
      .filter((item) => isWithinYearRange(item, yearFrom, yearTo))
    : [];

  const resumenFiltrado = {
    encuentros_nacionales_asistidos: encuentrosNacionalesFiltrados.length,
    encuentros_telematicos_asistidos: encuentrosTelematicosFiltrados.length,
    presentaciones_realizadas: presentacionesFiltradas.length,
    talleres_asistidos: talleresFiltrados.length,
    talleres_relatados: talleresRelatadosFiltrados.length,
    webinars_asistidos: webinarsFiltrados.length,
    reuniones_comite_asistidas: reunionesComiteFiltradas.length,
    reuniones_virtuales_asistidas: reunionesVirtualesFiltradas.length,
    cursos_realizados: cursosFiltrados.length,
  };

  const summaryCards = [
    ...REPORT_SUMMARY_CARDS
      .filter(({ content }) => isGeneralContent || selectedContent.includes(content))
      .map(({ key, singular, pluralLabel }) => ({
        value: resumenFiltrado[key],
        singular,
        pluralLabel,
      })),

    ...(showOnlyReuniones
      ? [
        {
          value: resumenFiltrado.reuniones_comite_asistidas,
          singular: "Reunión de comité asistida",
          pluralLabel: "Reuniones de comité asistidas",
        },
        {
          value: resumenFiltrado.reuniones_virtuales_asistidas,
          singular: "Reunión virtual asistida",
          pluralLabel: "Reuniones virtuales asistidas",
        },
      ]
      : []),
  ].filter(({ value }) => Number(value) > 0);

  const recursosCompartidos = (
    persona?.recursos_compartidos ??
    persona?.recursos_compartidos_lista ??
    []
  ).filter(Boolean);

  const comitePeriodo = String(persona?.comite_periodo ?? "")
    .trim()
    .replace(/^(\d{4})\D+\1$/, "$1");

  const hasComiteInfo = comitePeriodo && comitePeriodo !== "No corresponde";

  const comiteEstado =
    persona?.comite_estado ||
    (hasComiteInfo
      ? persona?.comite
        ? "Integrante del comité"
        : "Participación en comité"
      : null);

  const otherActivities = [
    isGeneralContent &&
    persona?.adjudico_fondo &&
    "Se adjudicó fondos para proyecto de investigación",

    isGeneralContent &&
    persona?.participa_sophia &&
    "Participó en número especial de Revista Sophia Austral",

    ...(isGeneralContent ? recursosCompartidos : []),

    isGeneralContent &&
    hasComiteInfo &&
    comiteEstado &&
    `${comiteEstado} RedFID durante el periodo ${comitePeriodo}`,

    showReuniones &&
    !showOnlyReuniones &&
    resumenFiltrado.reuniones_comite_asistidas > 0 &&
    `Ha asistido a ${resumenFiltrado.reuniones_comite_asistidas} ${plural(
      resumenFiltrado.reuniones_comite_asistidas,
      "reunión del comité RedFID",
      "reuniones del comité RedFID"
    )}`,
  ].filter(Boolean);

  const actividadesPorAnio = groupYearActivities({
    cursos: cursosFiltrados,
    encuentrosNacionales: encuentrosNacionalesFiltrados,
    presentaciones: presentacionesFiltradas,
    encuentrosTelematicos: encuentrosTelematicosFiltrados,
    talleres: talleresFiltrados,
    talleresRelatados: talleresRelatadosFiltrados,
    webinars: webinarsFiltrados,
    reunionesComite: reunionesComiteFiltradas,
    reunionesVirtuales: reunionesVirtualesFiltradas,
  });

  const paisNormalizado = normalizeText(persona?.pais);
  const isExtranjero =
    paisNormalizado &&
    !["chile", "—", "-", "sin informacion"].includes(paisNormalizado);

  const profileImageName = String(persona?.rawUser?.profile_image ?? "").trim();
  const staticUrl = String(process.env.REACT_APP_STATIC_URL ?? "").replace(/\/$/, "");
  const profileImageSrc =
    staticUrl &&
      profileImageName &&
      !["null", "undefined", "-", "—"].includes(normalizeText(profileImageName))
      ? `${staticUrl}/users/${persona.rawUser.id}/${profileImageName}`
      : null;

  const universidadesCount =
    persona?.universidad_keys?.length || countValues(persona?.universidad);

  const disciplinasCount = countValues(persona?.disciplina);
  const programasCount = countValues(persona?.programa);

  const yearsAsFormador =
    persona?.anio_formador && persona?.anio_formador !== "—"
      ? anioActual - Number(persona.anio_formador)
      : null;

  const standardGroups = [
    {
      title: "Pedagogía en Educación Básica",
      items: [persona?.estandar_basica, persona?.estandar_lenguaje]
        .filter(hasText)
        .flatMap((value) =>
          String(value)
            .split(/,\s*(?=Est[aá]ndar\s+[A-Z]:)/)
            .map((item) => item.trim())
        )
        .filter(hasText),
    },
    {
      title: "Pedagogía en Matemática",
      items: String(persona?.estandar_media ?? "")
        .split(/,\s*(?=Est[aá]ndar\s+[A-Z]:)/)
        .map((item) => item.trim())
        .filter(hasText),
    },
  ].filter((group) => group.items.length > 0);

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <ReportStyle disciplina={persona?.disciplina} />

        <View style={styles.content}>
          <ReportHeader
            title="Constancia de participación en RedFID"
            subtitle={persona.nombre_completo || persona.nombre}
          />

          <View style={styles.periodRow}>
            <Text style={styles.periodText}>
              <Text style={styles.subtitle}>Periodo del reporte: </Text>
              {getPeriodoLabel(yearFrom, yearTo, persona.disciplina)}
            </Text>

            <Text style={[styles.periodText, { textAlign: "right" }]}>
              <Text style={styles.subtitle}>Fecha de emisión: </Text>
              {fechaEmision}
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Información general</Text>

            <View style={styles.infoGeneralRow}>
              <View style={styles.photoCol}>
                <View style={styles.photoBox}>
                  {profileImageSrc ? (
                    <Image cache src={profileImageSrc} style={styles.profileImage} />
                  ) : (
                    <View style={styles.photoPlaceholder}>
                      <Svg viewBox="0 0 100 100" style={styles.photoPlaceholderIcon}>
                        <Circle
                          cx="50"
                          cy="50"
                          r="50"
                          fill={styles.photoPlaceholderIcon.color}
                        />
                        <Circle
                          cx="50"
                          cy="34"
                          r="13"
                          fill={styles.photoPlaceholderIcon.fill}
                        />
                        <Path
                          d="M24 78 C24 64, 35 57, 50 57 C65 57, 76 64, 76 78 L24 78 Z"
                          fill={styles.photoPlaceholderIcon.fill}
                        />
                      </Svg>
                    </View>
                  )}
                </View>
              </View>

              <View style={styles.infoCol}>
                <View style={styles.twoCols}>
                  <View style={styles.col}>
                    <FieldRow label="Nombre completo: ">
                      {valueOrDash(persona.nombre_completo)}
                    </FieldRow>

                    <FieldRow label="Título profesional: ">
                      {valueOrDash(persona.titulo_profesional)}
                    </FieldRow>

                    <FieldRow label="Grado académico: ">
                      {valueOrDash(persona.grado)}
                    </FieldRow>
                  </View>

                  <View style={styles.col}>
                    <FieldRow
                      label={plural(
                        universidadesCount,
                        "Universidad: ",
                        "Universidades: "
                      )}
                    >
                      {valueOrDash(persona.universidad)}
                    </FieldRow>

                    <FieldRow label="País: ">
                      {valueOrDash(persona.pais)}
                    </FieldRow>

                    {!isExtranjero ? (
                      <FieldRow label="Región: ">
                        {valueOrDash(persona.region_full)}
                      </FieldRow>
                    ) : null}
                  </View>
                </View>

                <View style={[styles.twoCols, { marginTop: 6 }]}>
                  <View style={styles.col}>
                    <FieldRow label="Año que se integra a RedFID: ">
                      {valueOrDash(persona.anio_union)}
                    </FieldRow>
                  </View>

                  <View style={styles.col}>
                    <FieldRow label="Años como formador/a: ">
                      {yearsAsFormador !== null
                        ? `${yearsAsFormador} ${plural(
                          yearsAsFormador,
                          "año",
                          "años"
                        )}`
                        : "—"}
                    </FieldRow>
                  </View>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Información académica</Text>

            <View style={{ marginBottom: 4 }}>
              <FieldRow
                label={plural(
                  disciplinasCount,
                  "Disciplina: ",
                  "Disciplinas: "
                )}
              >
                {valueOrDash(persona.disciplina)}
              </FieldRow>

              <FieldRow
                label={plural(
                  programasCount,
                  "Programa o carrera: ",
                  "Programas o carreras: "
                )}
              >
                {valueOrDash(persona.programa)}
              </FieldRow>
            </View>

            {standardGroups.length ? (
              <View style={styles.standardSection}>
                <Text style={styles.subYearTitle}>Estándares</Text>

                <View style={styles.standardGrid}>
                  {standardGroups.map((group) => (
                    <View key={group.title} style={styles.standardCol}>
                      <Text style={styles.standardTitle}>{group.title}</Text>

                      {group.items.map((item, idx) => (
                        <View
                          key={`${group.title}-${idx}`}
                          style={styles.summaryBulletRow}
                        >
                          <Text style={styles.summaryBullet}>•</Text>
                          <Text style={styles.summaryBulletText}>{item}</Text>
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              </View>
            ) : null}
          </View>

          {summaryCards.length || otherActivities.length ? (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Participación en RedFID</Text>

              {summaryCards.length ? (
                <View style={styles.summaryCardsGrid}>
                  {summaryCards.map((card) => (
                    <SummaryCard
                      key={card.singular}
                      value={card.value}
                      singular={card.singular}
                      pluralLabel={card.pluralLabel}
                    />
                  ))}
                </View>
              ) : null}

              {otherActivities.length ? (
                <View style={styles.summarySubsection}>
                  <Text style={styles.summarySubsectionTitle}>
                    Otras actividades RedFID
                  </Text>

                  {otherActivities.map((activity, idx) => (
                    <View
                      key={`other-activity-${idx}`}
                      style={styles.summaryBulletRow}
                    >
                      <Text style={styles.summaryBullet}>•</Text>
                      <Text style={styles.summaryBulletText}>{activity}</Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </View>
          ) : null}

          {actividadesPorAnio.length === 0 ? (
            <View wrap={false}>
              <Text style={styles.sectionTitle}>{activityByYearTitle}</Text>
              <Text style={styles.empty}>
                No registra actividades en el periodo seleccionado.
              </Text>
            </View>
          ) : (
            actividadesPorAnio.map((bloque, idx) => {
              const isFirstYear = idx === 0;
              const isLastYear = idx === actividadesPorAnio.length - 1;
              const firstSection = activeActivitySections.find(
                ({ key }) => bloque[key]?.length
              );

              return (
                <View
                  key={`year-${bloque.year}`}
                  style={isLastYear ? styles.yearBlockLast : styles.yearBlock}
                >
                  {firstSection ? (
                    <View wrap={false}>
                      {isFirstYear ? (
                        <Text style={styles.sectionTitle}>
                          {activityByYearTitle}
                        </Text>
                      ) : null}

                      <Text style={styles.yearTitle}>{bloque.year}</Text>
                      <Text style={styles.subYearTitle}>
                        {firstSection.firstTitle ?? firstSection.title}
                      </Text>
                      <ActivityItem
                        config={firstSection}
                        item={bloque[firstSection.key][0]}
                      />
                    </View>
                  ) : (
                    <View wrap={false}>
                      {isFirstYear ? (
                        <Text style={styles.sectionTitle}>
                          {activityByYearTitle}
                        </Text>
                      ) : null}

                      <Text style={styles.yearTitle}>{bloque.year}</Text>
                    </View>
                  )}

                  {activeActivitySections.map((config) => (
                    <ActivityGroup
                      key={config.key}
                      config={config}
                      items={bloque[config.key] ?? []}
                      skipFirst={firstSection?.key === config.key}
                    />
                  ))}
                </View>
              );
            })
          )}
        </View>

        <Text
          fixed
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          style={styles.pageNumber}
        />
      </Page>
    </Document>
  );
}